import { Injectable } from '@angular/core';
import { addDoc, collection, Firestore } from "@angular/fire/firestore";
import {EventService} from "./event.service";
import {AuthenticationService} from "./authentication.service";
import {OrganizationService} from "./organization.service";
import {Event} from "../models/event";
import {User} from "../models/user";
import {switchMap} from "rxjs/operators";
import {forkJoin} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  defaultHeader = '<!DOCTYPE html>\n' +
    '<html lang="nl">\n' +
    '  <head>\n' +
    '    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">\n' +
    '    <title>Steddie</title>\n' +
    '    <style type="text/css">\n' +
    '      h1, h2, td, p {\n' +
    '        font-family: helvetica, arial, sans-serif;\n' +
    '      }\n' +
    '    </style>\n' +
    '  </head>\n' +
    '  <body style="background-color: #f9fafb; padding: 20px;">\n' +
    '    <table style="background-color: #fff; width: 640px;">\n' +
    '      <tbody>\n' +
    '        <tr>\n' +
    '          <td>\n' +
    '            <table width="100%" cellspacing="0">\n' +
    '              <tbody>\n' +
    '                <tr>\n' +
    '                  <td valign="top" align="center">\n' +
    '                    <table width="100%" cellpadding="0" cellspacing="0">\n' +
    '                      <tbody>\n' +
    '                        <tr>\n' +
    '                          <td style="padding: 50px;">\n' +
    '                            <img style="height: 100px;" src="https://steddie.nl/assets/img/logo.webp" alt="Steddie Logo">\n' +
    '                            <span style="background: #f9fafb; height: 4px; display:block; margin-top: 30px;"></span>\n' +
    '                          </td>\n' +
    '                        </tr>\n' +
    '                      </tbody>\n' +
    '                    </table>\n' +
    '                  </td>\n' +
    '                </tr>\n' +
    '              </tbody>\n' +
    '            </table>\n' +
    '            <table width="100%" cellpadding="0" cellspacing="0" style="padding: 0 50px 50px">\n' +
    '              <tbody>\n' +
    '                <tr>\n' +
    '                  <td valign="top" align="center">\n' +
    '                    <table width="100%" cellpadding="0" border="0" cellspacing="0">\n' +
    '                      <tbody>\n' +
    '                        <tr>\n' +
    '                          <td style="font-size: 14px">';
  defaultFooter = '</td>\n' +
    '                        </tr>\n' +
    '                      </tbody>\n' +
    '                    </table>\n' +
    '                  </td>\n' +
    '                </tr>\n' +
    '              </tbody>\n' +
    '            </table>\n' +
    '          </td>\n' +
    '        </tr>\n' +
    '      </tbody>\n' +
    '    </table>\n' +
    '  </body>\n' +
    '</html>';

  constructor(
    private firestore: Firestore,
    private eventService: EventService,
    private orgService: OrganizationService,
    private authService: AuthenticationService
  ) { }

  /**
   * Sends an email based on the provided parameters.
   *
   * @param recipient
   * @param subject
   * @param body
   * @return uid
   */
  async sendEmail(recipient: string, subject: string, body: string): Promise<any> {
    await addDoc(collection(this.firestore, `mail`), {
      to: recipient,
      message: {
        subject,
        html: this.defaultHeader + body + this.defaultFooter,
        text: body
      }
    }).then(docRef => {
    }).catch(error => console.error(error));
  }

  /**
   * Sends two emails upon signing up for an activity, one to the user and one to the organization hosting the activity.
   *
   * @param eventID
   * @param userID
   */
  sendRegistrationEmail(eventID: string, userID: string) {
    let event: Event;
    let user: User;

    this.eventService.getEventByRef(eventID).pipe(
      switchMap(e => {
        event = e;
        return forkJoin([
          this.orgService.getOrganizationByRef(e.organization),
          this.authService.getUserDetails(userID)
        ]);
      })
    ).subscribe(([org, userDetails]) => {
      user = userDetails.data as User;

      let emailSubjectOrg = `Nieuwe aanmelding: ${event.name}`;
      let emailBodyOrg =
        `<h1 style="font-size:22px; color: #5d79c3; margin: 0;">
          Nieuwe aanmelding
        </h1>
        <br>
        <p>
          Beste,
        </p>
        <p>
          Er is een nieuwe aanmelding voor de activiteit: <a href='https://steddie.nl/activiteit/${event.id}'>${event.name}</a>.
        </p>
        <p>
          Ga naar het <a href='https://steddie.nl/activiteit/details/${event.id}'>overzicht</a> om de contactgegevens van de aangemelde gebruikers te bekijken.
        </p>
        <p>
          Met vriendelijke groet,<br>Het Steddie team
        </p>`
      this.sendEmail(org.email, emailSubjectOrg, emailBodyOrg);

      let emailSubjectUser = `Je aanmelding is gelukt`;
      let emailBodyUser =
        `<h1 style="font-size:22px; color: #5d79c3; margin: 0;">
          Aanmelding gelukt
        </h1>
        <br>
        <p>
          Beste ${user.name},
        </p>
        <p>
          Je aanmelding voor de activiteit: <a href='https://steddie.nl/activiteit/${event.id}'>${event.name}</a> is gelukt!
        </p>
        <p>
          Op de <a href="https://steddie.nl/profiel/activiteiten">profielpagina</a> kun je een overzicht terugvinden met daarin alle activiteiten waarvoor je bent aangemeld.
        </p>
        <p>
          De organisator van deze activiteit zal contact met je opnemen met daarin meer details over de activiteit.
        </p>
        <p>
          Met vriendelijke groet,<br>Het Steddie team
        </p>`
      this.sendEmail(user.email, emailSubjectUser, emailBodyUser);
    });
  }
}
